import { useEffect, useMemo, useRef, useState } from 'react'
import { Download } from 'lucide-react'

import { SalonDataTable } from '@/components/ui/SalonDataTable'
import { Button } from '@/components/ui/button'
import { defaultPaginationMeta } from '@/lib/api/masterCatalog'
import { exportRowsToCsv } from '@/lib/csvExport'

const EMPTY_EXTRA_PARAMS = Object.freeze({})

/**
 * @typedef {import('@/components/ui/SalonDataTable').SalonDataTableColumn} SalonDataTableColumn
 */

/**
 * @typedef {object} MasterServerTableExportColumn
 * @property {string} key
 * @property {string} label
 * @property {(row: object) => string | number | boolean | null | undefined} getValue
 */

/**
 * Server-paginated data table for master lists (Customers, Services, Staff, etc.).
 *
 * @param {object} props
 * @param {string} [props.title]
 * @param {SalonDataTableColumn[]} props.columns
 * @param {(params: { page: number, per_page: number, q: string, sort_by?: string, sort_dir?: 'asc' | 'desc' }) => Promise<{ items: object[], meta: import('@/types/api').PaginationMeta }>} props.fetchPage
 * @param {(row: object) => string | number} props.getRowKey
 * @param {(row: object) => void} [props.onEdit]
 * @param {string} [props.editLabel='Edit']
 * @param {MasterServerTableExportColumn[]} [props.exportColumns]
 * @param {string} [props.exportFilename='export.csv']
 * @param {(params: Record<string, unknown>) => Promise<object[]>} [props.fetchAllForExport]
 * @param {string} [props.emptyLabel='No records yet.']
 * @param {string} [props.emptyActionLabel]
 * @param {() => void} [props.onEmptyAction]
 * @param {string} [props.searchPlaceholder='Search…']
 * @param {number} [props.perPage=15]
 * @param {Record<string, unknown>} [props.extraParams]
 * @param {number} [props.reloadKey=0]
 * @param {import('react').ReactNode} [props.toolbar]
 * @param {import('react').ReactNode} [props.filters]
 */
export function MasterServerTable({
  title,
  columns,
  fetchPage,
  getRowKey,
  onEdit,
  editLabel = 'Edit',
  exportColumns,
  exportFilename = 'export.csv',
  fetchAllForExport,
  emptyLabel = 'No records yet.',
  emptyActionLabel,
  onEmptyAction,
  searchPlaceholder = 'Search…',
  perPage = 15,
  extraParams,
  reloadKey = 0,
  toolbar = null,
  filters = null,
}) {
  const [items, setItems] = useState([])
  const [meta, setMeta] = useState(defaultPaginationMeta())
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [sortBy, setSortBy] = useState('')
  const [sortDir, setSortDir] = useState('asc')
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const resolvedExtraParams = extraParams ?? EMPTY_EXTRA_PARAMS
  const extraParamsKey = useMemo(
    () => JSON.stringify(resolvedExtraParams),
    [resolvedExtraParams],
  )

  const fetchPageRef = useRef(fetchPage)
  fetchPageRef.current = fetchPage

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQ(q.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [q])

  useEffect(() => {
    setPage(1)
  }, [debouncedQ, sortBy, sortDir, reloadKey, extraParamsKey])

  useEffect(() => {
    let active = true

    async function load() {
      setLoading(true)
      setError('')

      const params = {
        page,
        per_page: perPage,
        q: debouncedQ || undefined,
        ...resolvedExtraParams,
      }

      if (sortBy) {
        params.sort_by = sortBy
        params.sort_dir = sortDir
      }

      try {
        const result = await fetchPageRef.current(params)
        if (!active) return
        setItems(Array.isArray(result.items) ? result.items : [])
        setMeta(defaultPaginationMeta(result.meta ?? {}))
      } catch (err) {
        if (!active) return
        setError(err?.response?.data?.message ?? 'Unable to load records.')
        setItems([])
        setMeta(defaultPaginationMeta())
      } finally {
        if (active) setLoading(false)
      }
    }

    load()

    return () => {
      active = false
    }
  }, [page, debouncedQ, sortBy, sortDir, reloadKey, extraParamsKey, perPage])

  function toggleSort(columnKey) {
    if (sortBy === columnKey) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortBy(columnKey)
    setSortDir('asc')
  }

  async function handleExport() {
    if (!exportColumns?.length) return
    setExporting(true)
    try {
      const rows = fetchAllForExport
        ? await fetchAllForExport({ q: debouncedQ || undefined, ...resolvedExtraParams })
        : items
      exportRowsToCsv(exportColumns, rows, exportFilename)
    } finally {
      setExporting(false)
    }
  }

  return (
    <SalonDataTable
      title={title}
      columns={columns}
      rows={items}
      loading={loading}
      error={error}
      getRowKey={getRowKey}
      searchPlaceholder={searchPlaceholder}
      searchValue={q}
      onSearchChange={setQ}
      sortBy={sortBy}
      sortDir={sortDir}
      onSortChange={toggleSort}
      meta={meta}
      onPageChange={setPage}
      onEdit={onEdit}
      editLabel={editLabel}
      emptyLabel={emptyLabel}
      emptyActionLabel={emptyActionLabel}
      onEmptyAction={onEmptyAction}
      toolbar={toolbar}
      filters={filters}
      compactSearch
      headerActions={exportColumns?.length ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={exporting || loading}
          className="gap-2 rounded-xl"
        >
          <Download className="h-4 w-4" />
          {exporting ? 'Exporting…' : 'CSV'}
        </Button>
      ) : null}
    />
  )
}

// Re-export column type for existing pages.
/** @typedef {SalonDataTableColumn} MasterServerTableColumn */
